// ระบบจัดการโปรไฟล์น้อง
// สร้างและจัดการข้อมูลโปรไฟล์ใน girls collection สำหรับ user ที่มี role เป็น girl

import { doc, setDoc, getDoc, updateDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * สร้างโปรไฟล์น้องใหม่
 * @param {string} userId - ID ของ user
 * @param {object} userData - ข้อมูล user จาก AuthContext
 * @param {object} additionalData - ข้อมูลเพิ่มเติม
 * @returns {Promise<boolean>}
 */
export const createGirlProfile = async (userId, userData, additionalData = {}) => {
  try {
    const girlRef = doc(db, 'girls', userId);
    
    // ตรวจสอบว่ามีโปรไฟล์อยู่แล้วหรือไม่
    const existingProfile = await getDoc(girlRef);
    if (existingProfile.exists()) {
      console.log(`✅ โปรไฟล์น้อง ${userId} มีอยู่แล้ว`);
      return true;
    }
    
    // สร้างโปรไฟล์ใหม่
    const profileData = {
      // ข้อมูลพื้นฐาน
      userId: userId, // เพิ่ม userId
      nickname: userData.displayName || 'น้องใหม่',
      email: userData.email,
      grades: 4.0, // ค่าเริ่มต้น
      type: 'normal', // ค่าเริ่มต้น
      height: 160, // ค่าเริ่มต้น
      weight: 50, // ค่าเริ่มต้น
      skinTone: 'fair', // ค่าเริ่มต้น
      
      // ข้อมูลราคา (ค่าเริ่มต้น)
      servicePrice: 1500,
      coinPrice: 50,
      commission: 0.3,
      prices: {
        base: 50,
        servicePrice: 1500,
        coinPrice: 50
      },
      
      // ข้อมูลสถานที่และติดต่อ
      location: 'กาญจนบุรี',
      bio: 'น้องใหม่ที่เพิ่งเข้าร่วมระบบ',
      lineType: 'self',
      lineContact: '',
      
      // ข้อมูลสถานะ
      status: 'available',
      images: [], // ไม่สร้าง default image เพื่อให้ tier free ไม่เต็มทันที
      
      // ข้อมูลการจัดการ
      momoId: null, // ยังไม่ได้เข้าร่วมทีม
      availability: {
        isAvailable: true
      },
      availabilityMode: 'always', // โหมดการรับงาน
      unavailableDates: [], // วันที่ไม่รับงาน
      
      // ข้อมูลเพิ่มเติม
      ...additionalData,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    await setDoc(girlRef, profileData);
    console.log(`✅ สร้างโปรไฟล์น้อง ${userId} สำเร็จ`);
    return true;
  } catch (error) {
    console.error(`❌ ไม่สามารถสร้างโปรไฟล์น้อง ${userId}:`, error);
    return false;
  }
};

/**
 * อัปเดตโปรไฟล์น้อง
 * @param {string} userId - ID ของ user
 * @param {object} updateData - ข้อมูลที่ต้องการอัปเดต
 * @returns {Promise<boolean>}
 */
export const updateGirlProfile = async (userId, updateData) => {
  try {
    const girlRef = doc(db, 'girls', userId);
    await updateDoc(girlRef, {
      ...updateData,
      updatedAt: new Date()
    });
    console.log(`✅ อัปเดตโปรไฟล์น้อง ${userId} สำเร็จ`);
    return true;
  } catch (error) {
    console.error(`❌ ไม่สามารถอัปเดตโปรไฟล์น้อง ${userId}:`, error);
    return false;
  }
};

/**
 * ดึงข้อมูลโปรไฟล์น้อง
 * @param {string} userId - ID ของ user
 * @returns {Promise<object|null>}
 */
export const getGirlProfile = async (userId) => {
  try {
    const girlRef = doc(db, 'girls', userId);
    const girlSnap = await getDoc(girlRef);
    
    if (girlSnap.exists()) {
      return { id: girlSnap.id, ...girlSnap.data() };
    }
    
    return null;
  } catch (error) {
    console.error(`❌ ไม่สามารถดึงโปรไฟล์น้อง ${userId}:`, error);
    return null;
  }
};

/**
 * สร้างโปรไฟล์น้องอัตโนมัติเมื่อเปลี่ยน role เป็น girl
 * @param {string} userId - ID ของ user
 * @param {object} userData - ข้อมูล user
 * @returns {Promise<boolean>}
 */
export const autoCreateGirlProfile = async (userId, userData) => {
  try {
    // ตรวจสอบว่าเป็น role girl หรือไม่
    if (userData.role !== 'girl') {
      return false;
    }
    
    // สร้างโปรไฟล์อัตโนมัติ
    const success = await createGirlProfile(userId, userData);
    
    if (success) {
      console.log(`✅ สร้างโปรไฟล์น้องอัตโนมัติสำเร็จ: ${userId}`);
    }
    
    return success;
  } catch (error) {
    console.error(`❌ ไม่สามารถสร้างโปรไฟล์น้องอัตโนมัติ ${userId}:`, error);
    return false;
  }
};

/**
 * อัปเดตข้อมูลน้องที่มีอยู่แล้วให้มี userId
 * @returns {Promise<boolean>}
 */
export const updateExistingGirlsWithUserId = async () => {
  try {
    const girlsRef = collection(db, 'girls');
    const snapshot = await getDocs(girlsRef);
    
    let updatedCount = 0;
    
    for (const docSnapshot of snapshot.docs) {
      const girlData = docSnapshot.data();
      const girlId = docSnapshot.id;
      
      // ถ้าไม่มี userId ให้เพิ่มเข้าไป
      if (!girlData.userId) {
        await updateDoc(doc(db, 'girls', girlId), {
          userId: girlId
        });
        updatedCount++;
        console.log(`✅ อัปเดต userId สำหรับน้อง ${girlId}`);
      }
    }
    
    console.log(`✅ อัปเดต userId สำหรับน้อง ${updatedCount} คน`);
    return true;
  } catch (error) {
    console.error('❌ ไม่สามารถอัปเดต userId:', error);
    return false;
  }
};

/**
 * ดึงรายการน้องที่ยังไม่ได้เข้าร่วมทีม
 * @returns {Promise<Array>}
 */
export const getAvailableGirls = async () => {
  try {
    const girlsRef = collection(db, 'girls');
    const q = query(girlsRef, where('momoId', '==', null));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('❌ ไม่สามารถดึงรายการน้องที่ว่าง:', error);
    return [];
  }
};

/**
 * ดึงรายการน้องทั้งหมด
 * @returns {Promise<Array>}
 */
export const getAllGirls = async () => {
  try {
    const girlsRef = collection(db, 'girls');
    const snapshot = await getDocs(girlsRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('❌ ไม่สามารถดึงรายการน้องทั้งหมด:', error);
    return [];
  }
};
